// Checkout Forms
import { resetAddressSearch } from 'checkout-forms/redux/actions/address/addressActions';
import { resetForm } from 'checkout-forms/redux/actions/form/formActions';
import { getFormInitialValuesFromAddress } from 'checkout-forms/utils/address/address';

// Config
import { navigateTo, navigateReplace } from '../navigation/navigationActions';
import { ageVerificationProductsSelector } from '@components/age-checker/AgeChecker.state';
import {
  ANALYTICS_DELIVERY_INSTRUCTIONS_SELECTED,
  ANALYTICS_DELIVERY_OPTIONS,
  ANALYTICS_GIFT_MESSAGE_POPULATED,
  ANALYTICS_GIFT_RECEIPT_SELECTED,
  ANALYTICS_PUT_DELIVERY_DETAILS,
  ANALYTICS_SHOW_AGE_CHECK_MODAL,
  ANALYTICS_UPDATED_PRODUCT_PAYMENT_DATA,
  CHANGE_DELIVERY_ADDRESS,
  EDIT_DELIVERY_ADDRESS,
  GET_DELIVERY_METHODS,
  GET_DELIVERY_PAGE,
  POST_DELIVERY_PAGE,
  PUT_DELIVERY_ADDRESS,
  EXPRESS_GET_DELIVERY_METHODS,
  EXPRESS_PUT_DELIVERY_DETAILS,
  PUT_DELIVERY_DETAILS,
  SILENTLY_SELECT_COLLECTION_DATE,
  RESET_DELIVERY_METHODS,
  SET_DELIVERY_BATCHES_PROCESSING,
  SET_SELECTED_DATE_METHOD,
  SET_SELECTED_DATE_METHOD_PAGE,
  SET_SELECTED_DELIVERY_CHOICE_ID,
  SET_SELECTED_SLOT_METHOD,
  SET_SHOW_DELIVERY_ADDRESS_FORM,
  USER_AGE_CHECK,
  ALLOW_INIT_DELIVERY_PAGE_CALLS,
  SET_FAKE_DELIVERY_ADDRESS_FOR_BATCHES,
  SET_HAS_VIEWED_STREAMLINED_DELIVERY_METHODS,
} from '@constants/actionConstants';
import deliveryConstants, { DELIVERY_DETAILS_FORM_ID } from '@constants/deliveryConstants';
import {
  URL_POST_NEW_DELIVERY_ADDRESS,
  URL_PUT_SAVED_DELIVERY_ADDRESS,
  URL_GET_DELIVERIES_METHODS_V4,
  URL_DELIVERY_PAGE,
  URL_PUT_DELIVERY_DETAILS,
} from '@constants/endpointConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import featureConstants from '@constants/featureConstants';
import routeConstants from '@constants/routeConstants';
import { createAddress } from '@redux/actions/addressBook/addressBookActions';
import {
  initSavedCollectionPoint,
  getCollectionPointDates,
  getSavedCollectionPoints,
  determinePreSelectedCollectionPoint,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import { triggerAnalyticsEvent, orderFormProductAnalyticsUpdate } from '@redux/actions/analytics/analyticsAction';
import { handleSubmitFormValidationErrorAnalytics, processForms } from '@redux/actions/form/formActions';
import {
  setScrollToDeliveryDetails,
  getConfiguration,
  recordCustomerInExperiment,
} from '@redux/actions/app/appActions';
import {
  recordInitDeliveryPageTimings,
  recordDeliveryMethodTimings,
  saveTimeContinueToPaymentClicked,
} from '@redux/actions/timing/timingActions';
import { canMakeApplePayPayments } from '@redux/actions/payment/applePayActions';
import { loadGooglePayScript } from '@redux/actions/payment/googlePayActions';
import {
  initPaymentPage,
  refreshItems,
  getOrderFormData,
  getOrderFormDeliveries,
  getOrderFormCollection,
} from '@redux/actions/payment/orderFormActions';
import { getOrderFormOrderNeedsToBeRebatchedSelector } from '@redux/reducers/order-form/orderFormSelector';
import { canMakePayPalPayExpressPayments } from '@redux/actions/payment/payPalPaymentActions';
import { fetchPaymentWallet } from '@redux/actions/user/userActions';
import { getDiscountsInBasketUIEnabledSelector } from '@redux/reducers/app/appSelector';
import {
  getConfigFeaturesSelector,
  isSinglePageCheckoutEnabledFeatureActive,
} from '@redux/reducers/config/configSelector';
import { getConfirmedDeliveryAddressSelector, getDeliveriesSelector } from '@redux/reducers/delivery/deliverySelector';
import { getRouterLocationPathname } from '@redux/reducers/router/routerSelector';
import { getIsSignedInWithDataSelector, getSelectedDeliveryAddressSelector } from '@redux/reducers/user/userSelector';
import { getDeliveryInstructions, isSameAddress, getGiftMessageLines } from '@utils';
import { getAddressFromFormValues } from '@utils/address/addressHelpers';
import isAddressValid from '@utils/addressBook/isAddressValid';
import { isClickAndCollectChoice, isDeliveryChoice, isGreenVanFleet } from '@utils/delivery/deliveryHelper';
import { getOutletFromDate } from '@utils/delivery/outlet';
import { DELIVERY_ADDRESS_FORM_ID } from '@utils/form/configs/deliveryAddress';
import daysBetweenDateAndStartOfWeek from '@utils/helpers/date/daysBetweenDateAndStartOfWeek';
import { isSecurePage } from '@utils/helpers/pageType';
import { isEmptyObject } from '@utils/object';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';

export const getDeliveryPage = (eventDetails) => async (dispatch, getState) => {
  const state = getState();
  const discountsInBasketUIEnabled = getDiscountsInBasketUIEnabledSelector(state);

  const deliveryPageResponse = dispatch({
    type: GET_DELIVERY_PAGE,
    request: (client) => client({ path: URL_DELIVERY_PAGE, config: { method: 'GET' } }),
    ...(eventDetails && { eventDetails }),
    discountsInBasketUIEnabled,
  });

  dispatch(getOrderFormDeliveries({ method: 'GET' }));

  return deliveryPageResponse;
};

const setUpdatedProductAndPaymentAnalytics =
  (deliveryMethodsResponse = []) =>
  (dispatch, getState) => {
    const oldPaymentAnalytics = window?.jlData?.checkout?.payment;
    const oldProductAnalytics =
      window?.jlData?.checkout?.product ?? getState().analytics?.analyticsData?.checkout?.product;

    const productAnalytics = deliveryMethodsResponse
      .map((response) =>
        (response?.result?.analytics?.checkout?.product ?? []).map((product) => {
          const foundProduct = oldProductAnalytics.filter((item) => item.sku === product.sku)[0];

          if (foundProduct) {
            return {
              ...foundProduct,
              batchNum: response.batchId,
            };
          }

          return product;
        }),
      )
      .flat();

    return dispatch({
      type: ANALYTICS_UPDATED_PRODUCT_PAYMENT_DATA,
      paymentAnalytics: oldPaymentAnalytics,
      productAnalytics,
    });
  };

export const postDeliveryPage = (isDeliveryPageInitialised) => async (dispatch, getState) => {
  dispatch(getOrderFormCollection());

  const state = getState();
  const discountsInBasketUIEnabled = getDiscountsInBasketUIEnabledSelector(state);

  // async dispatch configuration and delivery-page API calls.
  // Both must resolve successfully before we can start rendering delivery page
  const responses = await Promise.allSettled([
    dispatch(getConfiguration()),
    dispatch({
      // dispatch with NO_ACTION to avoid triggering side effects in reducers
      type: `${POST_DELIVERY_PAGE}_NO_ACTION`,
      request: (client) => client({ path: URL_DELIVERY_PAGE, config: { method: 'POST' } }),
      ...(isDeliveryPageInitialised && { isDeliveryPageInitialised }),
      discountsInBasketUIEnabled,
    }),
  ]);

  let deliveryPageResponse = responses.find((response) => response.value?.type?.includes(POST_DELIVERY_PAGE))?.value;

  // once both config and delivery-page Promise.allSettled has resolved, dispatch delivery page response to trigger
  // side effects in reducers
  deliveryPageResponse = {
    ...deliveryPageResponse,
    type: deliveryPageResponse?.type?.replace('_NO_ACTION', ''),
  };

  dispatch(deliveryPageResponse);

  if (deliveryPageResponse?.type?.includes('SUCCESS')) {
    await dispatch(setDeliveryAddressOnBasket());

    const orderFormResponses = await Promise.allSettled([
      dispatch(getOrderFormData()),
      dispatch(getOrderFormDeliveries()),
    ]);

    dispatch(
      orderFormProductAnalyticsUpdate(
        deliveryPageResponse?.result?.analytics?.checkout?.payment,
        orderFormResponses[0]?.value?.result?.analytics?.checkout?.product,
      ),
    );
  }

  return deliveryPageResponse;
};

export const clearDeliveryDetailsValidationErrors = (formId) => (dispatch, getState) => {
  dispatch(resetForm(formId, getState().form?.[formId]?.values ?? {}));
};

export const getDeliveryMethodsAnalytics = (deliveries) => (dispatch) => {
  const analytics = deliveries.map((delivery) => {
    if (!delivery.analytics?.deliveryProposition) {
      return null;
    }

    return {
      ...delivery.analytics,
      deliveryProposition: delivery.analytics.deliveryProposition.map((deliveryProposition, index) => {
        const { availabilityByDayDates, availabilityByDaySlots, ...otherProps } = deliveryProposition;

        const updatedDeliveryProposition = otherProps;

        if (availabilityByDayDates || availabilityByDaySlots) {
          // analytics expects an 'availabilityByDay' field but BFF returns either 'availabilityByDayDates' or
          // 'availabilityByDaySlots' depending on the type of calendar.
          // Slots should be an array of arrays, dates is a string
          updatedDeliveryProposition.availabilityByDay = availabilityByDayDates || [availabilityByDaySlots];
        }

        if (availabilityByDaySlots) {
          // GVF analytics expects 'dateRange' to be a string
          updatedDeliveryProposition.dateRange = delivery.dateRange.toString();
        }

        const selected = index === delivery.selectedMethod ? 1 : 0;

        return {
          ...updatedDeliveryProposition,
          selected,
        };
      }),
    };
  });

  dispatch(
    triggerAnalyticsEvent(ANALYTICS_DELIVERY_OPTIONS, {
      analytics: {
        checkout: {
          batch: analytics,
        },
      },
    }),
  );
};

export const updateDeliveryMethodAnalytics = () => async (dispatch, getState) => {
  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export function getBatchedDeliveryFormsWithErrorsIds(deliveries = [], state) {
  return deliveries.reduce((acc, batch) => {
    const formId = `${DELIVERY_DETAILS_FORM_ID}-${batch.id}`;
    const form = state.form?.[formId] ?? {};
    const hasErrors = !isEmptyObject(form.syncErrors);
    return hasErrors ? [...acc, formId] : acc;
  }, []);
}

export const setDeliveryBatchesProcessing = (isProcessing) => ({
  type: SET_DELIVERY_BATCHES_PROCESSING,
  isProcessing,
});

export const putDeliveryDetails = ({ deliveryDetailsPayload, express = false }) => ({
  type: express ? EXPRESS_PUT_DELIVERY_DETAILS : PUT_DELIVERY_DETAILS,
  request: (client) =>
    client({
      path: URL_PUT_DELIVERY_DETAILS,
      config: { method: 'PUT', body: { deliveries: deliveryDetailsPayload } },
    }),
});

export const getDeliveryDetailsForSubmit = ({ delivery, values = {}, giftReceipt = false, giftMessage = '' }) => {
  const { deliveryType } = delivery;

  const { deliveryMethod: formValueDeliveryMethod, ...rest } = values;
  const giftMessageLines = getGiftMessageLines({ giftMessage, giftReceipt });
  const formValues = {
    ...rest,
    deliveryMethod: formValueDeliveryMethod,
    giftMessageLines,
  };

  const isGreenVanFleetDelivery = isGreenVanFleet(deliveryType) || isGreenVanFleet(delivery.type);

  const deliveryInstructionsPresent = formValues.deliveryInstructionsOption?.length > 0;

  const deliveryInstructions =
    isGreenVanFleetDelivery && deliveryInstructionsPresent ? getDeliveryInstructions(formValues) : {};

  let deliveryDetails;
  let parsedValues;
  try {
    parsedValues = JSON.parse(values[deliveryConstants.DELIVERY_V3_FIELD]);

    deliveryDetails = {
      fulfilment: {
        fulfilmentOfferId: parsedValues.fulfilmentOfferId,
        ...(giftMessageLines && { giftMessageLines }),
        ...(!isEmptyObject(deliveryInstructions) && { deliveryInstructions }),
      },
    };
  } catch (_e) {
    return {};
  }

  // Get analytics details
  let analyticsEventDetails;

  const selectedMethod = delivery.methods[delivery.selectedMethod || 0];

  if (selectedMethod.fulfilmentOffers.type === deliveryConstants.DELIVERY_TYPE_STANDARD) {
    analyticsEventDetails = {
      deliveryMessage: selectedMethod.description.toLowerCase(),
    };
  } else {
    analyticsEventDetails = {
      deliveryMessage: selectedMethod.description.toLowerCase(),
      firstAvailableDay: daysBetweenDateAndStartOfWeek(
        selectedMethod.fulfilmentOffers.rows.map((r) => r.offers.find((o) => o.available))[0]?.date,
      ),
      selectedDaysUntilDelivery: daysBetweenDateAndStartOfWeek(parsedValues.date),
    };
  }

  return [
    {
      deliveryId: delivery.id,
      fulfilment: deliveryDetails.fulfilment,
    },
    { ...analyticsEventDetails },
  ];
};

export const submitDeliveryDetails = () => async (dispatch, getState) => {
  const state = getState();

  const deliveries = state.delivery.deliveries;

  const formsWithErrorsIds = getBatchedDeliveryFormsWithErrorsIds(deliveries, state);

  if (formsWithErrorsIds.length) {
    let formSyncErrors = {};

    formsWithErrorsIds.forEach((formId) => {
      formSyncErrors[formId] = state.form?.[formId]?.syncErrors;
    });

    const processFormsErrors = await dispatch(processForms(formsWithErrorsIds[0], formsWithErrorsIds));

    formSyncErrors = {
      ...processFormsErrors,
    };

    await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
    return;
  }

  dispatch(saveTimeContinueToPaymentClicked());
  dispatch(setDeliveryBatchesProcessing(true));

  const analyticsEventDetails = [];
  const deliveryDetailsPayload = [];

  deliveries.forEach((delivery) => {
    const formId = delivery.deliveryDetailsFormId;
    const form = state.form?.[formId] ?? {};
    const giftReceiptId = formId;

    const returnValues = getDeliveryDetailsForSubmit({
      delivery,
      giftMessage: state.app?.defaultGiftMessageValue?.[giftReceiptId],
      giftReceipt: state.app?.isGiftReceiptSelected?.[giftReceiptId],
      values: form.values,
    });

    deliveryDetailsPayload.push(returnValues[0]);
    if (returnValues.length > 0) {
      analyticsEventDetails.push(returnValues[1]);
    }
  });

  const putDeliveryDetailsResponse = await dispatch(putDeliveryDetails({ deliveryDetailsPayload }));

  if (putDeliveryDetailsResponse.type === `${PUT_DELIVERY_DETAILS}.SUCCESS`) {
    analyticsEventDetails.forEach((analytics) => {
      dispatch(
        triggerAnalyticsEvent(ANALYTICS_PUT_DELIVERY_DETAILS, {
          ...analytics,
        }),
      );
    });

    dispatch(
      initPaymentPage({
        pushAfter: true,
      }),
    );
  }

  if (
    putDeliveryDetailsResponse.type === `${PUT_DELIVERY_DETAILS}.FAILED` &&
    putDeliveryDetailsResponse?.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
  ) {
    const rebatchInProgress = state?.orderForm?.rebatchOrderApiCallActive;
    if (!rebatchInProgress) {
      await dispatch(handleInconsistentOrderFormState());
    }
  }

  dispatch(setDeliveryBatchesProcessing(false));
};

export const fakeDeliveryAddressForBatches = () => ({
  type: SET_FAKE_DELIVERY_ADDRESS_FOR_BATCHES,
  request: (client) =>
    client({
      path: URL_POST_NEW_DELIVERY_ADDRESS,
      config: {
        method: 'POST',
        body: {
          addressDetails: {
            addressee: {
              firstName: 'Fake',
              lastName: 'Name',
            },
            address: {
              addressLine1: '1 Drummond Gate',
              addressLine3: 'Pimlico',
              townOrCity: 'London',
              postcode: 'SW1V 2QQ',
              countryCode: 'GB',
            },
            phoneNumber: '07555555555',
            invalid: false,
          },
        },
      },
    }),
});

export const setDeliveryAddressOnBasket = () => async (dispatch, getState) => {
  // to be used after initial POST to delivery-page and after POST to rebatch order
  const selectedDeliveryAddress = getState()?.user?.selectedDeliveryAddress;
  if (selectedDeliveryAddress && !selectedDeliveryAddress?.invalid) {
    // customer already has a usable delivery address (e.g. their default address)
    // so re-apply it to the basket before getting deliveries
    return dispatch(
      putDeliveryAddress({
        body: {
          addressDetails: selectedDeliveryAddress,
        },
      }),
    );
  }

  // customer has no address so silently set a fake one so we can get
  // delivery batch information
  return dispatch(fakeDeliveryAddressForBatches());
};

export const putDeliveryAddress =
  ({ body, isSavedAddress = false }) =>
  (dispatch) => {
    const id = body.addressDetails?.id;
    const url = id ? URL_PUT_SAVED_DELIVERY_ADDRESS(id) : URL_POST_NEW_DELIVERY_ADDRESS;

    const method = id ? 'PUT' : 'POST';

    return dispatch({
      type: PUT_DELIVERY_ADDRESS,
      request: (client) => client({ path: url, config: { method, body } }),
      address: body.addressDetails,
      isSavedAddress,
    });
  };

export const getDeliveryMethods =
  ({ address = undefined, fromDate = undefined, id, refresh = false, dateRange = undefined, express = false }) =>
  async (dispatch, getState) => {
    if (refresh) {
      dispatch(resetForm(`${DELIVERY_DETAILS_FORM_ID}-${id}`, {}));
    }

    const outletFromDate = getOutletFromDate({ state: getState(), id, fromDate });
    const from = outletFromDate || fromDate;
    const path = URL_GET_DELIVERIES_METHODS_V4({
      from,
      id,
    });

    const response = await dispatch({
      address,
      type: express ? EXPRESS_GET_DELIVERY_METHODS : GET_DELIVERY_METHODS,
      batchId: id,
      ...(from && { fromDate: from }),
      ...(refresh && { refresh }),
      ...(dateRange && { dateRange }),
      mergeCustomerInfo: true,
      request: (client) =>
        client({
          path,
          config: {
            method: 'GET',
          },
        }),
    });

    if (response.type?.includes('SUCCESS') && !response.result?.methods?.length) {
      return {
        ...response,
        type: express ? `${EXPRESS_GET_DELIVERY_METHODS}.FAILED` : `${GET_DELIVERY_METHODS}.FAILED`,
        error: {},
      };
    }

    return response;
  };

export const refreshDeliveryMethods = () => async (dispatch, getState) => {
  const {
    confirmedDeliveryAddress,
    deliveries,
    deliveryContainsIncompatibleServices,
    undeliverableItemsInBasket,
    servicesThatNeedRemoving,
  } = getState().delivery;

  if (
    undeliverableItemsInBasket?.length > 0 ||
    deliveryContainsIncompatibleServices ||
    servicesThatNeedRemoving?.length > 0
  ) {
    // don't get delivery methods until undeliverable items in basket scenario is resolved
    return;
  }

  const getDeliveryMethodsResponse = await Promise.all(
    deliveries.map(async (delivery) => {
      const { id } = delivery;

      let fromDate = '';
      if (!delivery.selectedMethodIsExpress) {
        fromDate = delivery.fromDate;
      }

      const response = await dispatch(
        getDeliveryMethods({
          address: confirmedDeliveryAddress,
          fromDate,
          id,
        }),
      );

      if (
        response.type === `${GET_DELIVERY_METHODS}.FAILED` &&
        response?.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
      ) {
        await dispatch(handleInconsistentOrderFormState());
      }

      return response;
    }),
  );

  dispatch(setUpdatedProductAndPaymentAnalytics(getDeliveryMethodsResponse));

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setDeliveryAddressAndGetDeliveryMethods =
  ({ formValues, address }) =>
  (dispatch, getState) =>
    new Promise(async (resolve) => {
      const { app, delivery } = getState();

      const isSavedAddress = !!(address?.id || formValues?.id);
      const confirmedDeliveryAddress = delivery?.confirmedDeliveryAddress;
      const shouldSetConfirmedDeliveryAddressAgain = delivery?.shouldSetConfirmedDeliveryAddressAgain;
      const disableSaveDeliveryAddress = (app?.orderComplete ?? false) || (app?.orderProcessing ?? false);

      if (disableSaveDeliveryAddress) {
        resolve({});
        return;
      }

      if (
        !shouldSetConfirmedDeliveryAddressAgain &&
        isSavedAddress &&
        isSameAddress({ formValues, address, addressToCheck: confirmedDeliveryAddress })
      ) {
        resolve({});
        return;
      }

      const addressFromFormValues = getAddressFromFormValues(formValues);
      const body = address ? { addressDetails: address } : { addressDetails: addressFromFormValues };

      if (body.addressDetails.invalid) {
        resolve({});
        return;
      }

      const lineItemTotalBeforePutAddress = getState()?.orderForm?.lineItemTotal;

      const putDeliveryAddressResponse = await dispatch(
        putDeliveryAddress({
          body,
          isSavedAddress,
        }),
      );

      if (putDeliveryAddressResponse.type?.includes('SUCCESS')) {
        // re-create delivery batches after new address is used
        await dispatch(getOrderFormDeliveries());

        const state = getState();

        const {
          delivery: {
            deliveries,
            undeliverableItemsInBasket,
            deliveryContainsIncompatibleServices,
            servicesThatNeedRemoving,
          } = {},
        } = state;

        await dispatch(refreshItems(lineItemTotalBeforePutAddress));

        const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);

        if (
          undeliverableItemsInBasket?.length > 0 ||
          deliveryContainsIncompatibleServices ||
          servicesThatNeedRemoving?.length > 0
        ) {
          // don't get delivery methods until undeliverable items in basket scenario is resolved
          dispatch(navigateTo(singlePageCheckoutEnabled ? routeConstants.SECURE : routeConstants.DELIVERY));
          resolve();
          return;
        }

        const getDeliveryMethodsResponse = await Promise.all(
          deliveries.map(async (delivery) => {
            dispatch(resetForm(delivery.deliveryDetailsFormId));
            const deliveryMethods = await dispatch(
              getDeliveryMethods({
                address: isEmptyObject(address) ? addressFromFormValues : address,
                id: delivery.id,
                type: delivery.type,
                isSavedAddress,
              }),
            );

            return deliveryMethods;
          }),
        );

        dispatch(setUpdatedProductAndPaymentAnalytics(getDeliveryMethodsResponse));

        dispatch(getDeliveryMethodsAnalytics(deliveries));
      }

      resolve(putDeliveryAddressResponse);
    });

export const resetDeliveryMethods = () => ({
  type: RESET_DELIVERY_METHODS,
});

export const resetDeliveryAddressAndMethods =
  ({ addressId, countryCode }) =>
  async (dispatch) => {
    dispatch(resetDeliveryMethods());
    dispatch(resetAddressSearch({ addressId, countryCode }));
  };

export const initSignedInUser = () => async (dispatch, getState) => {
  const user = getState().user || {};
  if (user.isSignedInWithData) {
    const selectedDeliveryAddress = user.selectedDeliveryAddress;

    if (selectedDeliveryAddress) {
      if (!selectedDeliveryAddress.invalid && !selectedDeliveryAddress.notAvailableForDelivery) {
        await dispatch(setDeliveryAddressAndGetDeliveryMethods({ address: selectedDeliveryAddress }));
      }
    }
  }
};

export const setCollectionUrl = (redirectAction) => async (dispatch, getState) => {
  const pathname = getRouterLocationPathname();

  if (pathname === routeConstants.EDIT_BASKET) {
    return {};
  }

  // TODO: NEEDS SOME STATE TO DECIDE DELIVERY VS CLICK AND COLLECT
  if (!pathname?.includes(routeConstants.CLICK_AND_COLLECT)) {
    const selectedCollectionPointId = getState()?.clickAndCollect?.selectedCollectionPoint?.id;
    let url;
    if (selectedCollectionPointId) {
      url = routeConstants.CLICK_AND_COLLECT_STORE_SELECTED;
    } else {
      url = routeConstants.CLICK_AND_COLLECT_SEARCH_LIST;
    }

    return dispatch(navigateTo(url, { replace: redirectAction === 'replace' }));
  }

  return {};
};

// TODO a lot of common functionality between initDelivery and setSelectedDeliveryChoiceId, could possibly be merged
export const initDelivery =
  ({ refresh = false, suppressRouteRedirects = false } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectedDeliveryChoiceId = state?.delivery?.selectedDeliveryChoiceId;
    const pathname = getRouterLocationPathname();
    const configFeatures = getConfigFeaturesSelector(state);
    const hasLoadedConfigFeatures = configFeatures?.length > 0;

    // Single Page Checkout has specific routing requirements, so we need to check if it's enabled before redirecting to the secure route or delivery route
    const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);
    const isSecurePath = isSecurePage({ pathname });
    const shouldRetainSecureRoute = singlePageCheckoutEnabled || (isSecurePath && !hasLoadedConfigFeatures);

    if (singlePageCheckoutEnabled && !isSecurePath && !suppressRouteRedirects) {
      await dispatch(navigateReplace(routeConstants.SECURE));
    }

    if (isDeliveryChoice(selectedDeliveryChoiceId)) {
      // if delivery methods exist in state we can assume
      // user has manually changed URL to get here, so don't redirect
      if (
        !state.delivery?.deliveries?.some((delivery) => delivery.methods) &&
        !shouldRetainSecureRoute &&
        !suppressRouteRedirects
      ) {
        await dispatch(navigateReplace(singlePageCheckoutEnabled ? routeConstants.SECURE : routeConstants.DELIVERY));
      }

      const confirmedDeliveryAddress = state?.delivery?.confirmedDeliveryAddress;
      const getDeliveryMethodsError = state?.delivery?.getDeliveryMethodsError;
      const putDeliveryAddressError = state?.delivery?.putDeliveryAddressError;

      if (!getDeliveryMethodsError && !putDeliveryAddressError) {
        const hasConfirmedDeliveryAddress = !!confirmedDeliveryAddress;

        if (hasConfirmedDeliveryAddress) {
          const shouldSetConfirmedDeliveryAddressAgain = state?.delivery?.shouldSetConfirmedDeliveryAddressAgain;

          if (shouldSetConfirmedDeliveryAddressAgain) {
            dispatch(setScrollToDeliveryDetails(true));
            await dispatch(
              setDeliveryAddressAndGetDeliveryMethods({
                address: confirmedDeliveryAddress,
              }),
            );
          } else {
            await dispatch(refreshDeliveryMethods());
            dispatch(recordDeliveryMethodTimings());
          }
        } else {
          dispatch(initSignedInUser());
        }
      }
    }

    if (isClickAndCollectChoice(selectedDeliveryChoiceId)) {
      let datesAlreadyLoaded = true;

      if (state.user?.collectionPointsSaved && !state.user.savedCollectionPointsLoaded) {
        const collectionPoint = determinePreSelectedCollectionPoint(state?.user?.collectionPoints ?? []);

        await Promise.all([
          dispatch(getSavedCollectionPoints()),
          dispatch(getCollectionPointDates(collectionPoint?.id)),
        ]);

        // if we couldn't get dates for the desired collection point, set datesAlreadyLoaded to false
        // so we can try again later in the journey when the collection point is properly selected
        const datesAvailable = getState().clickAndCollect?.collectionPointDates?.length > 0;
        if (!datesAvailable) datesAlreadyLoaded = false;
      }

      if (!singlePageCheckoutEnabled) {
        await dispatch(setCollectionUrl('replace'));
      }

      dispatch(initSavedCollectionPoint({ refresh, datesAlreadyLoaded }));
    }

    if (selectedDeliveryChoiceId) dispatch(fetchPaymentWallet());
  };

export const setSelectedDeliveryChoiceId = (id) => async (dispatch, getState) => {
  const state = getState();
  const deliveryMethodsApiCallComplete = state.delivery?.deliveryMethodsApiCallComplete;
  const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);

  dispatch({
    type: SET_SELECTED_DELIVERY_CHOICE_ID,
    id,
    resetUseDeliveryAddressAsBillingAddress: state?.delivery?.selectedDeliveryChoiceId !== id,
  });

  if (id === deliveryConstants.DELIVERY) {
    dispatch(navigateTo(singlePageCheckoutEnabled ? routeConstants.SECURE : routeConstants.DELIVERY));
    dispatch(initDelivery());
  }

  // TODO: Needs resolving once click and collect is considered
  if (id === deliveryConstants.CLICK_AND_COLLECT) {
    if (state.user?.collectionPointsSaved && !state.user?.savedCollectionPointsLoaded) {
      await dispatch(getSavedCollectionPoints());
    }

    if (singlePageCheckoutEnabled) {
      dispatch(navigateTo(routeConstants.SECURE));
    } else {
      await dispatch(setCollectionUrl());
    }
    dispatch(initSavedCollectionPoint());

    const selectedCollectionPointId = state?.clickAndCollect?.selectedCollectionPoint?.id;
    if (deliveryMethodsApiCallComplete && selectedCollectionPointId) {
      const fetchedCollectionPointDates = await dispatch(getCollectionPointDates());

      const newSelectedCollectionDateObject = fetchedCollectionPointDates.result.collectionDates.find(
        (item) => item.date === state.clickAndCollect.selectedCollectionTime,
      );

      if (newSelectedCollectionDateObject) {
        dispatch({
          type: SILENTLY_SELECT_COLLECTION_DATE,
          payload: newSelectedCollectionDateObject.date,
        });
      }
    }
  }
};

export const postDeliveryActions = () => async (dispatch) => {
  dispatch(loadGooglePayScript());
  dispatch(canMakeApplePayPayments());
  dispatch(canMakePayPalPayExpressPayments());
};

export const initDeliveryPage =
  ({
    forceGetDeliveryPage = false,
    isDeliveryPageInitialised = false,
    refresh = false,
    suppressRouteRedirects = false,
  } = {}) =>
  async (dispatch, getState) => {
    const state = getState();

    const deliveries = getDeliveriesSelector(state) || [];

    const preventApiRefresh =
      deliveries?.some((delivery) => delivery.methods) &&
      deliveries?.length !== 0 &&
      !getOrderFormOrderNeedsToBeRebatchedSelector(state);

    if (preventApiRefresh) {
      // If we have deliveries and methods already in state, and the order doesnt need to be rebatched, we don't need to re-fetch the delivery page.
      return;
    }
    // occurs if browser is refreshed OR user navigates via browser controls
    const postOnly = !forceGetDeliveryPage && (getState()?.delivery?.usePostToInitDeliveryPage ?? true);
    const response = await dispatch(postOnly ? postDeliveryPage(isDeliveryPageInitialised) : getDeliveryPage());

    if (response.type === `${GET_DELIVERY_PAGE}.SUCCESS` || response.type === `${POST_DELIVERY_PAGE}.SUCCESS`) {
      if (postOnly) dispatch(recordInitDeliveryPageTimings());

      const ageVerificationProducts = ageVerificationProductsSelector(getState());
      if (ageVerificationProducts?.length > 0) {
        return;
      }

      const { showAgeCheckModal, userDobInvalid, userDob } = getState().user || {};

      if (showAgeCheckModal) {
        dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_AGE_CHECK_MODAL));
        if (userDobInvalid) {
          dispatch({
            type: `${USER_AGE_CHECK}.FAILED`,
            dob: userDob,
          });
        }
      }

      dispatch(postDeliveryActions());

      await dispatch(initDelivery({ refresh, suppressRouteRedirects }));
    }

    if (
      response.type === `${GET_DELIVERY_PAGE}.FAILED` &&
      response?.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
    ) {
      await dispatch(handleInconsistentOrderFormState());
    }
  };

export const resetIsDeliveryPageInitialised = () => ({
  type: ALLOW_INIT_DELIVERY_PAGE_CALLS,
});

export const getDeliveryMethodsDateRange =
  ({ id = '', fromDate = '', refresh = false, dateRange = 0 }) =>
  async (dispatch, getState) => {
    await dispatch(
      getDeliveryMethods({
        id,
        fromDate,
        refresh,
        dateRange,
      }),
    );

    dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
  };

export const setSelectedSlotMethod = (displayMethod, express, delivery) => (dispatch, getState) => {
  dispatch({
    type: SET_SELECTED_SLOT_METHOD,
    displayMethod,
    express,
    delivery,
  });

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setSelectedDateMethod = (displayMethod, deliveryId) => (dispatch, getState) => {
  dispatch({
    type: SET_SELECTED_DATE_METHOD,
    displayMethod,
    deliveryId,
  });

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setSelectedDateMethodPage = ({ delivery, method, newPage, shouldAnimate }) => ({
  type: SET_SELECTED_DATE_METHOD_PAGE,
  delivery,
  method,
  newPage,
  shouldAnimate,
});

export const deliveryInstructionsOptionSelected = (selection) => (dispatch) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_DELIVERY_INSTRUCTIONS_SELECTED, { subType: selection }));
};

export const editDeliveryAddress = (address) => async (dispatch) => {
  dispatch(resetForm(DELIVERY_ADDRESS_FORM_ID, getFormInitialValuesFromAddress(address)));
  dispatch({ type: EDIT_DELIVERY_ADDRESS });
};

export const changeDeliveryAddress = () => async (dispatch, getState) => {
  const state = getState();
  const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);
  const isSignedInWithData = getIsSignedInWithDataSelector(state);

  if (isSignedInWithData) {
    const route = singlePageCheckoutEnabled
      ? routeConstants.SECURE_DELIVERY_ADDRESS_BOOK
      : routeConstants.DELIVERY_ADDRESS_BOOK;
    dispatch(navigateTo(route));
    return;
  }

  if (!isSignedInWithData) {
    const confirmedDeliveryAddress = getConfirmedDeliveryAddressSelector(state);
    const selectedDeliveryAddress = getSelectedDeliveryAddressSelector(state);
    const address = confirmedDeliveryAddress || selectedDeliveryAddress;
    const initValues = getFormInitialValuesFromAddress(address);

    dispatch(resetForm(DELIVERY_ADDRESS_FORM_ID, initValues));
    dispatch({ type: CHANGE_DELIVERY_ADDRESS });
  }
};

export const setShowDeliveryAddressForm = (show) => ({
  type: SET_SHOW_DELIVERY_ADDRESS_FORM,
  show,
});

export const giftReceiptSelected = (isGiftReceiptSelected) => (dispatch) => {
  dispatch(
    triggerAnalyticsEvent(ANALYTICS_GIFT_RECEIPT_SELECTED, {
      giftReceiptSelected: isGiftReceiptSelected,
    }),
  );
};

export const giftMessagePopulated = (isGiftMessagePopulated) => (dispatch) => {
  dispatch(
    triggerAnalyticsEvent(ANALYTICS_GIFT_MESSAGE_POPULATED, {
      giftMessagePopulated: isGiftMessagePopulated,
    }),
  );
};

export const onUseAddressButtonClick = (formValues) => async (dispatch, getState) => {
  const isSignedIn = getState().user.isSignedIn;
  const selectedDeliveryAddress = formValues
    ? getAddressFromFormValues(formValues)
    : getState().user.selectedDeliveryAddress;

  // double-check address is valid in case customer managed to slip past the net
  if (!isAddressValid(selectedDeliveryAddress)) return;

  dispatch(setScrollToDeliveryDetails(true));
  if (isSignedIn && !selectedDeliveryAddress.id) {
    return dispatch(
      createAddress({
        addressPayload: {
          addressee: selectedDeliveryAddress.addressee,
          address: selectedDeliveryAddress.address,
          phoneNumber: selectedDeliveryAddress.phoneNumber,
        },
      }),
    );
  }

  return dispatch(
    setDeliveryAddressAndGetDeliveryMethods({
      address: selectedDeliveryAddress,
    }),
  );
};

export const recordStreamlinedDeliveryMethodsImpressionId = () => (dispatch, getState) => {
  const { config: { features = [] } = {} } = getState() ?? {};

  const featureFlag = features?.find((feature) => feature.id === featureConstants.STREAMLINED_DELIVERY_METHOD);
  const impressionId = featureFlag?.impressionId;

  if (impressionId) {
    dispatch(recordCustomerInExperiment(impressionId));
  }
};

export const setHasViewedStreamlinedDeliveryMethods = () => ({
  type: SET_HAS_VIEWED_STREAMLINED_DELIVERY_METHODS,
});
